import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { sendTelemetryDocument } from '../api/data-connectors/telemetry'
import { getFileExtensionFromName, isSupportedFileType } from '../utils'
import { useNotification } from './useNotification'
import { useUploadFileService } from './useUploadFileService'

/**
 * Hook for uploading multiple files with validation.
 * Each file is validated independently; unsupported types are skipped with a notification.
 * Valid files are uploaded in parallel (fire-and-forget).
 */
export function useUploadFiles() {
  const { uploadFile } = useUploadFileService()
  const { notify } = useNotification()
  const { t } = useTranslation()

  const uploadFiles = useCallback(
    (files: FileList | File[]) => {
      Array.from(files).forEach((file) => {
        const extension = getFileExtensionFromName(file.name)
        if (!isSupportedFileType(extension)) {
          notify({ text: t('files.invalidFileType', { filename: file.name }), isSuccess: false })
          return
        }

        uploadFile(file)
        // The browser only gives the file name, never the folder it came from.
        sendTelemetryDocument('upload', file.name)
      })
    },
    [uploadFile, notify, t]
  )

  return { uploadFiles }
}
